import * as i4 from '@upr-web-primer/app-utils';
import { paramNum, param, paramBool, DEFAULT_RETRY_STRATEGY, genericRetryStrategy, AutoSubscription, AppEnvironmentService } from '@upr-web-primer/app-utils';
import { __decorate, __metadata } from 'tslib';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import * as i2 from '@upr-web-primer/spinner';
import { SPINNER_KEYS, SpinnerService } from '@upr-web-primer/spinner';
import * as i3 from '@upr-web-primer/storage';
import { LocalStorageService } from '@upr-web-primer/storage';
import { WINDOW } from '@upr-web-primer/window';
import { merge, find, get } from 'lodash-es';
import { firstValueFrom, timer, throwError } from 'rxjs';
import { retry, delay, catchError } from 'rxjs/operators';

const OIDC_REPLACED_DEFAULTS = {
    oidc: {
        refresh_expires_in_seconds: paramNum('$oidc.refresh_expires_in_seconds$'),
        app_flow_path: param('$oidc.app_flow_path$'),
        authorize_enabled: paramBool('$oidc.authorize_enabled$'),
        authorize_path: param('$oidc.authorize_path$'),
        base_url: param('$oidc.base_url$'),
        base_url_secondary: param('$oidc.base_url_secondary$'),
        client_id: param('$oidc.client_id$'),
        client_secret: param('$oidc.client_secret$'),
        enabled: paramBool('$oidc.enabled$'),
        end_session: param('$oidc.end_session$'),
        node_api_base_url: param('$oidc.node_api_base_url$'),
        password_flow_path: param('$oidc.password_flow_path$'),
        redirect_enabled: paramBool('$oidc.redirect_enabled$'),
        redirect_url: param('$oidc.redirect_url$'),
    },
};
const OIDC_ENV_DEFAULTS = {
    oidc: {
        refresh_expires_in_seconds: 86400,
        app_flow_path: '/oidc/connect/token',
        authorize_enabled: false,
        authorize_path: '/account/api/oidc/authorize',
        base_url: '',
        base_url_secondary: '',
        client_id: '',
        client_secret: '',
        enabled: true,
        end_session: '/oidc/connect/endsession',
        node_api_base_url: '/account/api/oidc',
        password_flow_path: '/oidc/connect/token',
        redirect_enabled: false,
        redirect_url: '/oidc/connect/authorize',
    },
};

let OidcService = class OidcService {
    constructor(window, httpClient, spinner, localStorage, appEnvService) {
        this.window = window;
        this.httpClient = httpClient;
        this.spinner = spinner;
        this.localStorage = localStorage;
        this.appEnvService = appEnvService;
        this.cache = new Map();
        this.spinnerToken = 'LoginOIDC';
        this.defaultState = 'web';
        this.appEnvService.addDefaultValues(OIDC_ENV_DEFAULTS, OIDC_REPLACED_DEFAULTS);
        this.isLocalhost = new RegExp(/localhost|web-local/, 'gi').test(this.window.document.location.hostname);
        this.subscriptions.add(this.appEnvService.envValues$.subscribe((values) => {
            this.envConfig = values['oidc'];
            if (this.isLocalhost && (this.envConfig.base_url || '') !== '/api') {
                const isApiBaseUrlAlreadySet = !this.envConfig.base_url.includes('http');
                this.envConfig.base_url =
                    this.window.document.location.origin +
                        (isApiBaseUrlAlreadySet ? this.envConfig.base_url : '/api');
            }
        }));
        this.config = {
            retry: 4,
            retry_delay: 500,
            headers: {},
        };
    }
    setConfig(configurations) {
        this.config = merge(configurations, this.config);
    }
    getConfig() {
        return this.config;
    }
    get token() {
        return this._token;
    }
    set token(theToken) {
        this._token = theToken;
    }
    getAppToken() {
        const cacheKey = 'APP_TOKEN';
        const checkCache = this.cache.has(cacheKey);
        if (!checkCache) {
            const postData = `grant_type=client_credentials&scope=default`;
            const options = {
                headers: {
                    Authorization: `Basic ` +
                        this.window.btoa(`${this.envConfig.client_id}:${this.envConfig.client_secret}`),
                    Accept: `application/json`,
                    'Cache-Control': 'no-cache',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            };
            merge(options.headers, this.config.headers);
            const promise = firstValueFrom(this.httpClient
                .post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options)
                .pipe(retry(this.config.retry), delay(this.config.retry_delay), catchError(this.handleError))).finally(() => {
                firstValueFrom(timer(500)).then(() => {
                    this.cache.delete(cacheKey);
                });
            });
            this.cache.set(cacheKey, promise);
            return promise;
        }
        return this.cache.get(cacheKey);
    }
    getAuthToken() {
        if (!this.config.username || !this.config.password) {
            throw new Error('To use Password Flow, you must provide username and password');
        }
        const postData = `grant_type=password&scope=default openid offline_access&username=${this.config.username}&password=${this.config.password}`;
        const options = {
            headers: {
                Authorization: `Basic ` +
                    this.window.btoa(`${this.envConfig.client_id}:${this.envConfig.client_secret}`),
                Accept: `application/json`,
                'Cache-Control': 'no-cache',
                username: this.config.username,
                'subj-claim': 'default',
                'aud-claim': 'default',
                'iss-claim': 'default',
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        };
        merge(options.headers, this.config.headers);
        const customRetry = DEFAULT_RETRY_STRATEGY;
        customRetry.customFunc = (error, retryObj, attempt) => {
            if (error?.error === 'invalid_client' ||
                error?.error?.error === 'invalid_client') {
                return throwError(() => error);
            }
            return timer(attempt * retryObj.scalingDuration);
        };
        const retryStrategy = genericRetryStrategy(customRetry);
        return firstValueFrom(this.httpClient
            .post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options)
            .pipe(retry(retryStrategy), catchError(this.errorHandler)));
    }
    getRefreshToken(currentToken) {
        const cacheKey = 'REFRESH_TOKEN';
        const checkCache = this.cache.has(cacheKey);
        if (!checkCache) {
            const postData = `grant_type=refresh_token&scope=openid default offline_access&refresh_token=${currentToken}`;
            const options = {
                headers: {
                    Authorization: 'Basic ' +
                        this.window.btoa(this.envConfig.client_id + ':' + this.envConfig.client_secret),
                    Accept: 'application/json',
                    'Cache-Control': 'no-cache',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            };
            merge(options.headers, this.config.headers);
            const promise = firstValueFrom(this.httpClient.post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options)).finally(() => {
                firstValueFrom(timer(500)).then(() => {
                    this.cache.delete(cacheKey);
                });
            });
            this.cache.set(cacheKey, promise);
            return promise;
        }
        return this.cache.get(cacheKey);
    }
    getTokensByCode(currentCode, redirectUri) {
        const cacheKey = 'AUTHORIZATION_TOKENS';
        const checkCache = this.cache.has(cacheKey);
        if (!checkCache) {
            const postData = `grant_type=authorization_code&code=${currentCode}&redirect_uri=${redirectUri}`;
            const options = {
                headers: {
                    Authorization: 'Basic ' +
                        this.window.btoa(this.envConfig.client_id + ':' + this.envConfig.client_secret),
                    Accept: 'application/json',
                    'Cache-Control': 'no-cache',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            };
            merge(options.headers, this.config.headers);
            const promise = firstValueFrom(this.httpClient.post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options)).finally(() => {
                firstValueFrom(timer(500)).then(() => {
                    this.cache.delete(cacheKey);
                });
            });
            this.cache.set(cacheKey, promise);
            return promise;
        }
        return this.cache.get(cacheKey);
    }
    Login(username, password, redirectUri, is_corp) {
        if (!username || !password || !redirectUri) {
            return Promise.reject('invalid params');
        }
        this.spinner.show(this.spinnerToken);
        return this.getLoginCsrefToken(is_corp).then((_csrf) => {
            let httpHeaders = new HttpHeaders();
            httpHeaders = this.setNoCacheHeaders(httpHeaders);
            httpHeaders = httpHeaders.append('Accept', 'text/html');
            httpHeaders = httpHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
            httpHeaders = httpHeaders.append('enctype', 'multipart/form-data');
            const bodyRequest = `scope=openid default&grant_type=password&username=${username}&password=${password}&client_id=${this.envConfig.client_id}&state=${_csrf}&redirect_uri=${redirectUri}&nonce=${this.uid()}&is_corp=${this.isCorpString(is_corp)}`;
            return firstValueFrom(this.httpClient.post(this.envConfig.app_flow_path, bodyRequest, {
                headers: httpHeaders,
                responseType: 'text',
                observe: 'response',
            }))
                .then((response) => {
                const oidcResponse = this.getOidcResponse(response.url);
                this.spinner.hide(this.spinnerToken);
                return Promise.resolve(oidcResponse);
            })
                .catch((error) => this.errorHandler(error));
        });
    }
    LoginByIdToken(idToken, redirectUri, is_corp) {
        const url = `${this.envConfig.app_flow_path}?response_type=id_token token&scope=openid default&client_id=${this.envConfig.client_id}&redirect_uri=${redirectUri || this.window.location.href}&nonce=${this.uid()}&state=${this.defaultState}+&id_token_hint=${idToken}&is_corp=${this.isCorpString(is_corp)}`;
        let httpHeaders = new HttpHeaders();
        httpHeaders = this.setNoCacheHeaders(httpHeaders);
        httpHeaders = httpHeaders.append('Accept', 'text/html');
        const httpOptions = {
            headers: httpHeaders,
            responseType: 'text',
            observe: 'response',
        };
        return firstValueFrom(this.httpClient.get(url, httpOptions))
            .then((response) => {
            const oidcResponse = this.getOidcResponse(response.url);
            return Promise.resolve(oidcResponse);
        })
            .catch((error) => this.errorHandler(error));
    }
    getLoginCsrefToken(is_corp) {
        const url = `${this.envConfig.app_flow_path}?response_type=id_token token&scope=openid default&client_id=${this.envConfig.client_id}&redirect_uri=${this.window.location.href}&nonce=${this.uid()}&state=${this.defaultState}&is_corp=${this.isCorpString(is_corp)}`;
        let httpHeaders = new HttpHeaders();
        httpHeaders = this.setNoCacheHeaders(httpHeaders);
        httpHeaders = httpHeaders.append('Accept', 'text/html');
        const httpOptions = {
            headers: httpHeaders,
            responseType: 'text',
        };
        return firstValueFrom(this.httpClient.get(url, httpOptions))
            .then((response) => Promise.resolve(this.getCsrf(response)))
            .catch((error) => Promise.reject(error));
    }
    uid() {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
    isCorpString(is_corp) {
        return !!is_corp ? 'true' : 'false';
    }
    setNoCacheHeaders(httpHeaders) {
        httpHeaders = httpHeaders.append('x-uip', 'Web');
        httpHeaders = httpHeaders.append('x-source-id', 'Web');
        httpHeaders = httpHeaders.append('Pragma', 'no-cache');
        httpHeaders = httpHeaders.append('Cache-Control', 'no-store');
        return httpHeaders;
    }
    getCsrf(response) {
        let _csrf = '';
        try {
            const el = document.createElement('html');
            el.innerHTML = response;
            const inputElements = el.getElementsByTagName('input');
            const stateEle = find(inputElements, (ele) => ele.getAttribute('name') === 'state');
            if (stateEle) {
                _csrf = stateEle.getAttribute('value') || '';
            }
        }
        catch { }
        return _csrf;
    }
    getDecodedAccessToken(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(this.window
                .atob(base64)
                .split('')
                .map((c) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
                .join(''));
            return JSON.parse(jsonPayload);
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }
    getOidcResponse(input) {
        const id_token = this.getParameterByName(input, 'id_token');
        if (!id_token) {
            return '';
        }
        this.localStorage.setItem('OIDC_ID_TOKEN', id_token);
        const jwt = this.getDecodedAccessToken(id_token);
        return {
            id_token: id_token,
            access_token: this.getParameterByName(input, 'access_token'),
            expires_in: this.getParameterByName(input, 'expires_in'),
            token_type: this.getParameterByName(input, 'token_type'),
            state: this.getParameterByName(input, 'state'),
            gid: get(jwt, 'sub', ''),
        };
    }
    errorHandler(error) {
        if (error &&
            error.status === 404 &&
            error.url &&
            error.url.contains('id_token')) {
            const oidcResponse = this.getOidcResponse(error.url);
            if (oidcResponse) {
                return Promise.resolve(oidcResponse);
            }
        }
        let message = 'generic error';
        if (error &&
            error.status === 401 &&
            error.error &&
            error.error.httpMessage === 'Unauthorized') {
            message = 'invalid username';
        }
        this.spinner.hide(SPINNER_KEYS.OVERRIDE);
        return Promise.reject(message);
    }
    handleError() {
        return throwError(() => new Error('Failed to get token after retries'));
    }
    getParameterByName(input, name) {
        const match = RegExp('[?&]' + name + '=([^&]*)').exec(input);
        return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
    }
    ngOnDestroy() {
    }
};
OidcService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: OidcService, deps: [{ token: WINDOW }, { token: i1.HttpClient }, { token: i2.SpinnerService }, { token: i3.LocalStorageService }, { token: i4.AppEnvironmentService }], target: i0.ɵɵFactoryTarget.Injectable });
OidcService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: OidcService, providedIn: 'root' });
OidcService = __decorate([
    AutoSubscription(),
    __metadata("design:paramtypes", [Object, HttpClient,
        SpinnerService,
        LocalStorageService,
        AppEnvironmentService])
], OidcService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: OidcService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i1.HttpClient }, { type: i2.SpinnerService }, { type: i3.LocalStorageService }, { type: i4.AppEnvironmentService }]; } });

export { OIDC_ENV_DEFAULTS, OidcService };
